// Form "Lengkapkan Data" untuk guru — melengkapkan No. KP + Telefon Orangtua
// murid di bawah jagaannya. Guna RPC guru_student_data_status /
// guru_update_student_data (migrasi 008). No. KP tidak pernah dipaparkan
// semula atas sebab privasi — hanya badge "Sudah diisi".
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { Button } from './ui/button';
import { CenterMorphModal, CenterMorphModalContent } from './beui/CenterMorphModal';

const inputCls = 'w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand';

export default function StudentDataForm({
  student, open, onOpenChange, onSaved,
  rpcStatus = 'guru_student_data_status',
  rpcUpdate = 'guru_update_student_data',
  showNoKpValue = false,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [noKp, setNoKp] = useState('');
  const [telefon, setTelefon] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open || !student) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDone(false);
    setNoKp('');
    setTelefon('');
    setStatus(null);
    (async () => {
      const { data, error: e } = await supabase.rpc(rpcStatus, { p_student_id: student.id });
      if (cancelled) return;
      if (e) {
        setError(e.message);
      } else {
        setStatus(data);
        setTelefon(data?.telefon_orangtua || '');
        if (showNoKpValue) setNoKp((data?.no_kp || '').replace(/[^\d]/g, '').slice(0, 12));
      }
      setLoading(false);
    })();
  return () => { cancelled = true; };
  }, [open, student, rpcStatus, showNoKpValue]);

  const save = async () => {
    if (noKp !== '' && noKp.length !== 12) {
      setError(t('noKp12Digit'));
      return;
    }
    setSaving(true);
    setError('');
    const { error: e } = await supabase.rpc(rpcUpdate, {
      p_student_id: student.id,
      p_no_kp: noKp,
      p_telefon_orangtua: telefon,
    });
    setSaving(false);
    if (e) {
      setError(e.message);
    } else {
      setDone(true);
      onSaved && onSaved();
    }
  };

  const noKpTerisi = showNoKpValue
    ? !!(status?.no_kp && status.no_kp !== '')
    : status?.no_kp_terisi;

  return (
    <CenterMorphModal open={open} onOpenChange={onOpenChange}>
      <CenterMorphModalContent ariaLabel={t('lengkapkanData')} className="max-w-md bg-card">
        <div className="p-6">
          <h2 className="text-lg font-extrabold text-foreground">{t('lengkapkanData')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{student?.nama}</p>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('memuatkan')}
            </div>
          ) : done ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
              <p className="mt-3 font-bold text-foreground">{t('dataDisimpan')}</p>
              <Button className="mt-5 h-10 px-6" onClick={() => onOpenChange(false)}>{t('tutup')}</Button>
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-muted-foreground">{t('noKp')}</label>
                  {noKpTerisi && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="h-3 w-3" />{t('noKpTerisi')}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={noKp}
                  onChange={(e) => setNoKp(e.target.value.replace(/[^\d]/g, '').slice(0, 12))}
                  placeholder={noKpTerisi && !showNoKpValue ? t('noKpGantiPh') : t('noKpPh')}
                  inputMode="numeric"
                  className={inputCls}
                />
                <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" />{t('noKpPrivasi')}
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">{t('telefonOrangtua')}</label>
                <input
                  type="tel"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  placeholder={t('telefonPh')}
                  className={inputCls}
                />
              </div>

              {error && (
                <p className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-2.5 text-xs text-red-700 dark:text-red-300 break-words">{error}</p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>{t('batal')}</Button>
                <Button className="h-10 bg-brand text-white hover:bg-brand-royal" disabled={saving} onClick={save}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t('simpan')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </CenterMorphModalContent>
    </CenterMorphModal>
  );
}
